/**
 * Thin wrapper over Web Speech API (`SpeechRecognition` /
 * `webkitSpeechRecognition`). Streams interim and final transcripts via
 * callbacks. Returns null when the API isn't available (Firefox, some
 * embedded webviews).
 *
 * Privacy note: on Chromium-based browsers this ships audio to Google's
 * servers for recognition. Safari on iOS 14.1+ also uses an upstream
 * service. The NewIdea screen surfaces this to the user.
 */

import { DebugLog } from "../debug/log";

const TAG = "util.speech";

interface SpeechRecognitionResult {
  isFinal: boolean;
  0: { transcript: string };
}

interface SpeechRecognitionEvent {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResult>;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message?: string;
}

interface RecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type RecognitionCtor = new () => RecognitionLike;

declare global {
  interface Window {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  }
}

function ctor(): RecognitionCtor | null {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

export function isSpeechRecognitionAvailable(): boolean {
  return ctor() !== null;
}

export interface SpeechRecognizer {
  start(): void;
  stop(): void;
}

export interface SpeechCallbacks {
  onPartial(text: string): void;
  onFinal(text: string): void;
  onStart(): void;
  onEnd(): void;
  onError(message: string): void;
}

export function createSpeechRecognizer(
  callbacks: SpeechCallbacks,
  options?: { lang?: string },
): SpeechRecognizer | null {
  const Ctor = ctor();
  if (!Ctor) return null;
  const rec = new Ctor();
  rec.continuous = true;
  rec.interimResults = true;
  rec.lang = options?.lang ?? (typeof navigator !== "undefined" ? navigator.language : "en-US");

  rec.onstart = () => {
    DebugLog.i(TAG, "recognition started");
    callbacks.onStart();
  };

  rec.onresult = (event) => {
    let interim = "";
    let final = "";
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      const text = result[0]?.transcript ?? "";
      if (result.isFinal) final += text;
      else interim += text;
    }
    if (final.trim().length > 0) {
      DebugLog.d(TAG, "final segment", final.trim());
      callbacks.onFinal(final.trim());
    }
    if (interim.trim().length > 0) {
      callbacks.onPartial(interim.trim());
    }
  };

  rec.onerror = (event) => {
    DebugLog.w(TAG, `recognition error: ${event.error}`, event.message);
    callbacks.onError(event.error || "speech recognition error");
  };

  rec.onend = () => {
    DebugLog.i(TAG, "recognition ended");
    callbacks.onEnd();
  };

  return {
    start: () => {
      try {
        rec.start();
      } catch (e) {
        DebugLog.w(TAG, "start() threw", e instanceof Error ? e.message : String(e));
        callbacks.onError(e instanceof Error ? e.message : String(e));
      }
    },
    stop: () => {
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    },
  };
}
